import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {Field, Form, Formik} from 'formik'
import {inject, observer} from "mobx-react"
import {useState} from "react"
import {Container, Row, Col} from "react-bootstrap"
import FormGroup from 'react-bootstrap/FormGroup'
import FormLabel from 'react-bootstrap/FormLabel'
import FormControl from 'react-bootstrap/FormControl'
import {WithTranslation, withTranslation} from "react-i18next"
import {useParams} from 'react-router-dom'
import {MerchantAdminApi} from "../../Api"
import {
  MultiLanguageFormikTitleField,
  MultiLanguageFormikDescriptionField
} from "../../components/Formik"
import {RewardModifyLocationParamsProps, RewardsLocation} from "../../Locations"
import AlertMessage from "../../components/AlertMessage"
import {VenueSelectionDropdown} from "../../components/VenueSelectionDropdown"
import {IRewardStore, REWARD_TYPE} from "../../models/RewardStore"
import {IUserSessionStore} from "../../models/UserSessionStore"
import {useLanguageService} from "../../utils/languageContext"
import SelectDropdown from '../../components/SelectDropdown'
import ImageUploadSection from '../../components/ImageUploadSection'
import ToggleWithModal from '../../components/ToggleWithModal'
import {uploadImage, ImageType} from '../../utils/imageUpload'
import FormModals from '../../components/FormModals'
import {FormActionButtons} from '../../components/FormActionButtons'
import {FormHeader} from '../../components/FormHeader'
import {FormSection} from '../../components/FormSection'
import SaveAndPublishButton from '../../components/SaveAndPublishButton'
import {
  getRewardValidationSchema,
  getRewardDefaultValues,
  transformRewardValues,
  prepareRewardForSubmission,
  NEW_REWARD_ID_PREFIX
} from './formUtils'

const MODIFY_REWARD_ERROR = "modifyRewardError"
const CREATE_UPDATE_REWARD_ERROR = "createUpdateRewardError"

export interface CreateUpdateRewardScreenProps extends WithTranslation {
  api?: MerchantAdminApi
  userSessionStore?: IUserSessionStore
  rewardStore?: IRewardStore
  history?: any
}

const CreateUpdateRewardScreen = (props: CreateUpdateRewardScreenProps) => {
  const [showGoBackModal, setShowGoBackModal] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const {t, userSessionStore, history, api} = props
  const languageService = useLanguageService();

  const params = useParams<RewardModifyLocationParamsProps>()

  const selectedRewardId = params.rewardId
  const selectedRewardType = params.rewardType
  const selectedReward = props.rewardStore?.rewards?.find(reward => reward.id === selectedRewardId)

  const contentLanguages = languageService.getContentLanguages();

  const validationSchema = getRewardValidationSchema(t, languageService);
  
  const initValues = selectedReward 
    ? transformRewardValues(selectedReward, contentLanguages)
    : getRewardDefaultValues(userSessionStore!, languageService);

  const handleSaveAndPublish = async (values: any) => {
    setSaveError(null);
    try {
      const rewardTrigger = props.rewardStore?.rewardTriggers?.find(trigger => trigger.type === selectedRewardType)
      const rewardTriggerId = rewardTrigger?.id

      if (!rewardTriggerId) {
        console.error('No reward trigger ID found');
        setSaveError(t('rewardModifyScreen.errors.missingTrigger'));
        return;
      }

      const postData = prepareRewardForSubmission(values, rewardTriggerId);
      const isNewReward = values.id === NEW_REWARD_ID_PREFIX;
      
      const response = await api!.postReward(postData, undefined, CREATE_UPDATE_REWARD_ERROR)
      if (response) {
        if (isNewReward) {
          props.rewardStore?.addReward(response)
        } else {
          props.rewardStore?.updateReward(response)
        }
        history.push(RewardsLocation.toUrl(userSessionStore!.selectedBranch!.urlFields()))
      }
    } catch (error) {
      console.error('Error saving reward:', error);
      setSaveError(t('rewardModifyScreen.errors.saveError'));
    }
  }

  const handleUploadImage = async (imageFile: any) => {
    return uploadImage(api, imageFile, ImageType.VENUE_PRODUCT_MAIN, 'reward');
  };

  const onBackToRewards = (isFormEdited: boolean) => {
    if (isFormEdited)
      setShowGoBackModal(true)
    else
      history.push(RewardsLocation.toUrl(userSessionStore!.selectedBranch!.urlFields()))
  }

  const getFormTitle = () => {
    switch (selectedRewardType) {
      case REWARD_TYPE.REWARD_JOIN:
        return t("rewardsScreen.signUpReward")
      case REWARD_TYPE.REWARD_BIRTHDAY:
        return t("rewardsScreen.birthdayGift")
      default:
        return 'Unknown reward type'
    }
  }

  return (
    <Container>
      {saveError && (
        <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
          <FontAwesomeIcon className="me-2" icon={["fas", "exclamation-circle"]} />
          <div>{saveError}</div>
        </div>
      )}
      
      <AlertMessage source={MODIFY_REWARD_ERROR} />

      <Formik
        initialValues={initValues}
        validationSchema={validationSchema}
        onSubmit={values => {
          handleSaveAndPublish(values);
        }}
      >
        {({ errors, touched, values, dirty, handleChange, resetForm, setFieldValue }) => (
          <Form>
            <FormModals
              showGoBackModal={showGoBackModal}
              showRevertModal={showRevertModal}
              onHideGoBackModal={() => setShowGoBackModal(false)}
              onHideRevertModal={() => setShowRevertModal(false)}
              onConfirmGoBack={() => history.push(RewardsLocation.toUrl(userSessionStore!.selectedBranch!.urlFields()))}
              onConfirmRevert={() => {
                resetForm();
                setShowRevertModal(false);
              }}
            />

            <FormActionButtons
              onBack={() => onBackToRewards(dirty)}
              onRevert={() => setShowRevertModal(true)}
              onSubmit={() => {}}
              isFormDirty={dirty}
              backButtonText={t("stampCardModifyScreen.backToRewards")}
            />

            <FormHeader 
              title={getFormTitle()}
              description={t(`rewardModifyScreen.formDescription`).split('\n')}
            />

            {!userSessionStore!.currentOrganization!.isSingleVenue() && (
              <FormSection title={t('rewardModifyScreen.venues')}>
                <Row>
                  <Col md={6}>
                    <VenueSelectionDropdown
                      assignedVenues={values.assigned_venues}
                      setFieldValue={setFieldValue}
                      venues={userSessionStore!.currentOrganization!.venues!}
                      fieldName="assigned_venues"
                    />
                  </Col>
                </Row>
              </FormSection>
            )}

            <FormSection title={t('rewardModifyScreen.sectionTitleMessaging')}>
              <Row>
                <Col md={6}>
                  <Field
                    name={`message`}
                    render={() => (
                      <FormGroup controlId={`message`}>
                        <FormLabel
                          className={`textInputLabel ${!!(errors.message && touched.message) ? 'text-danger' : ''}`}>
                          {t(`rewardModifyScreen.messageField`)}
                          <div className="mb-2">
                            <small>{t('rewardModifyScreen.messageFieldDescription')}</small>
                          </div>
                        </FormLabel>
                        <FormControl
                          className="textInput"
                          as='textarea'
                          rows={4}
                          isInvalid={!!(errors.message && touched.message)}
                          placeholder={t(`rewardModifyScreen.messageFieldPlaceholder`)}
                          value={values.message}
                          onChange={handleChange}
                        />
                        {!!(errors.message && touched.message) ? (
                          <small className="text-danger">{errors.message}</small>
                        ) : null}
                      </FormGroup>
                    )}
                  />
                </Col>
              </Row>
            </FormSection>

            <FormSection title={t('rewardModifyScreen.reward')}>
              <Row>
                <Col md={6}>
                  <MultiLanguageFormikTitleField
                    baseName="title"
                    errors={errors}
                    touched={touched}
                    values={values}
                    setFieldValue={setFieldValue}
                    placeholderTextKey="rewardModifyScreen.titlePlaceholder"
                    labelTextKey="rewardModifyScreen.titleField"
                    showLabel={true}
                  />
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <MultiLanguageFormikDescriptionField
                    baseName="description"
                    errors={errors}
                    touched={touched}
                    values={values}
                    setFieldValue={setFieldValue}
                    placeholderTextKey="descriptionPlaceholder"
                  />
                </Col>
              </Row>

              <ImageUploadSection
                name="images.mainImage"
                values={values}
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
                onImageUpload={handleUploadImage}
              />

              <AlertMessage source="imageUploadError" />

              <Row className="align-items-center mt-3">
                <Col md={3}>
                  <Container className="p-0">
                    <Row>
                      <Col md={12}>
                        <div className="mb-2">
                          <div style={{ fontSize: 14 }}>{t('rewardModifyScreen.rewardExpirationInDays')}</div>
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={8}>
                        <SelectDropdown
                          name="timing_rules.validity.duration"
                          label={t('rewardModifyScreen.rewardExpirationInDays')}
                          values={values}
                          errors={errors}
                          touched={touched}
                          onChange={handleChange}
                          setFieldValue={setFieldValue}
                          options={[
                            { value: 7, label: '7' },
                            { value: 14, label: '14' },
                            { value: 30, label: '30' },
                            { value: 60, label: '60' },
                            { value: 90, label: '90' },
                            { value: 180, label: '180' }
                          ]}
                          disabled={Boolean(selectedReward?.time_frame_reached)}
                        />
                      </Col>
                    </Row>
                  </Container>
                </Col>
              </Row>
            </FormSection>

            <FormGroup controlId="active">
              <ToggleWithModal
                name="active"
                value={values.active}
                onValue={false}
                offValue={true}
                onChange={(newStatus) => setFieldValue('active', newStatus)}
                label={t("rewardModifyScreen.hideRewardFromUsers")}
                modalTitle={t("rewardModifyScreen.modals.hideReward.title")}
                modalDescription={t("rewardModifyScreen.modals.hideReward.description")}
                modalConfirmText={t("manageVenuesScreen.hide")}
                modalCancelText={t("manageVenuesScreen.cancel")}
                className="mt-5"
              />
            </FormGroup>

            <Row className="mt-4">
              <Col md={12}>
                <div className="float-right">
                  <SaveAndPublishButton />
                </div>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </Container>
  )
}

export default withTranslation()(inject("userSessionStore", "api", "rewardStore")(observer(CreateUpdateRewardScreen))) 